(() => {
  const SELECTORS = {
    header: '[data-polene-header]',
    menu: '[data-polene-mobile-menu]',
    search: '[data-polene-search-drawer]',
    country: '[data-polene-country-drawer]',
  };

  const TRANSITION_MS = 420;

  function getDrawer(name) {
    return document.querySelector(SELECTORS[name]);
  }

  function syncBodyState() {
    const openDrawer = document.querySelector('.polene-drawer.is-open');
    document.body.classList.toggle('polene-drawer-open', Boolean(openDrawer));
  }

  function closeDrawer(drawer, restoreFocus = true) {
    if (!drawer || drawer.hidden) return;
    const triggerSelector = drawer.dataset.restoreFocus;
    drawer.classList.remove('is-open');

    window.setTimeout(() => {
      drawer.hidden = true;
      syncBodyState();
      if (restoreFocus && triggerSelector) {
        document.querySelector(triggerSelector)?.focus();
      }
    }, TRANSITION_MS);
  }

  function closeAllDrawers(except = null) {
    document.querySelectorAll('.polene-drawer.is-open').forEach((drawer) => {
      if (drawer !== except) closeDrawer(drawer, false);
    });
  }

  function openDrawer(drawer, trigger) {
    if (!drawer) return;
    closeAllDrawers(drawer);
    drawer.hidden = false;
    if (trigger) {
      if (!trigger.id) trigger.id = `polene-trigger-${Math.random().toString(36).slice(2, 9)}`;
      drawer.dataset.restoreFocus = `#${trigger.id}`;
    }
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      syncBodyState();
      window.setTimeout(() => {
        const focusTarget = drawer.querySelector('[data-polene-search-input], button, a, input');
        focusTarget?.focus({ preventScroll: true });
      }, 60);
    });
  }

  function setMenuButtonState(open) {
    document.querySelectorAll('[data-polene-menu-open]').forEach((button) => {
      button.setAttribute('aria-expanded', String(open));
    });
  }

  function closeMobileMenuPanel(panel) {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    const trigger = document.querySelector(`[aria-controls="${panel.id}"][data-polene-mobile-panel-open]`);
    trigger?.setAttribute('aria-expanded', 'false');

    window.setTimeout(() => {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    }, TRANSITION_MS);
  }

  function resetMobileMenuPanels() {
    const drawer = getDrawer('menu');
    if (!drawer) return;
    drawer.querySelectorAll('[data-polene-submenu]').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      panel.hidden = true;
    });
    drawer.querySelectorAll('[data-polene-mobile-panel-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function closeMobileMenu(restoreFocus = true) {
    const drawer = getDrawer('menu');
    resetMobileMenuPanels();
    setMenuButtonState(false);
    closeDrawer(drawer, restoreFocus);
  }

  function bindMobileMenuPanels() {
    const drawer = getDrawer('menu');
    if (!drawer || drawer.dataset.polenePanelsBound === 'true') return;
    drawer.dataset.polenePanelsBound = 'true';

    drawer.querySelectorAll('[data-polene-mobile-panel-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel || !drawer.contains(panel)) return;

        drawer.querySelectorAll('[data-polene-submenu]').forEach((other) => {
          if (other !== panel) closeMobileMenuPanel(other);
        });
        drawer.querySelectorAll('[data-polene-mobile-panel-open]').forEach((otherTrigger) => {
          otherTrigger.setAttribute('aria-expanded', String(otherTrigger === trigger));
        });

        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');
        panel.scrollTop = 0;
        window.requestAnimationFrame(() => {
          panel.classList.add('is-open');
          window.setTimeout(() => panel.querySelector('[data-polene-mobile-panel-back]')?.focus({ preventScroll: true }), 60);
        });
      });
    });

    drawer.querySelectorAll('[data-polene-mobile-panel-back]').forEach((button) => {
      button.addEventListener('click', () => {
        closeMobileMenuPanel(button.closest('[data-polene-submenu]'));
      });
    });
  }

  function bindDrawers() {
    document.querySelectorAll('[data-polene-menu-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = getDrawer('menu');
        const willOpen = drawer?.hidden ?? true;
        if (willOpen) {
          setMenuButtonState(true);
          openDrawer(drawer, button);
        } else {
          closeMobileMenu();
        }
      });
    });

    document.querySelectorAll('[data-polene-menu-close]').forEach((button) => {
      button.addEventListener('click', () => closeMobileMenu());
    });

    document.querySelectorAll('[data-polene-search-open]').forEach((button) => {
      button.addEventListener('click', () => {
        closeMobileMenu(false);
        openDrawer(getDrawer('search'), button);
      });
    });

    document.querySelectorAll('[data-polene-search-close]').forEach((button) => {
      button.addEventListener('click', () => closeDrawer(getDrawer('search')));
    });

    document.querySelectorAll('[data-polene-country-open]').forEach((button) => {
      button.addEventListener('click', () => {
        closeMobileMenu(false);
        openDrawer(getDrawer('country'), button);
      });
    });

    document.querySelectorAll('[data-polene-country-close]').forEach((button) => {
      button.addEventListener('click', () => closeDrawer(getDrawer('country')));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const menuPanel = getDrawer('menu')?.querySelector('[data-polene-submenu].is-open');
      if (menuPanel) {
        closeMobileMenuPanel(menuPanel);
        return;
      }
      const drawer = document.querySelector('.polene-drawer.is-open');
      if (drawer) {
        if (drawer.matches(SELECTORS.menu)) closeMobileMenu();
        else closeDrawer(drawer);
      }
      document.querySelectorAll('[data-polene-menu-details][open]').forEach((details) => details.removeAttribute('open'));
    });
  }

  function bindDesktopMenus() {
    const detailsItems = [...document.querySelectorAll('[data-polene-menu-details]')];

    detailsItems.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          detailsItems.forEach((other) => {
            if (other !== details) other.removeAttribute('open');
          });
        }
        document.body.classList.toggle('polene-menu-open', detailsItems.some((item) => item.open));
      });
    });

    document.addEventListener('click', (event) => {
      if (detailsItems.some((details) => details.contains(event.target))) return;
      detailsItems.forEach((details) => details.removeAttribute('open'));
      document.body.classList.remove('polene-menu-open');
    });
  }

  function bindHeaderScroll() {
    const header = document.querySelector(SELECTORS.header);
    if (!header) return;

    let frame = 0;
    let lastScrolled = null;
    const update = () => {
      frame = 0;
      const isScrolled = window.scrollY > 10;
      if (isScrolled === lastScrolled) return;
      lastScrolled = isScrolled;
      header.classList.toggle('is-scrolled', isScrolled);
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
  }

  function bindWishlist() {
    let saved = [];
    try {
      const parsed = JSON.parse(localStorage.getItem('polene-wishlist-items') || '[]');
      saved = Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (_) {
      saved = [];
    }

    const render = () => {
      document.querySelectorAll('[data-polene-wishlist-count]').forEach((node) => {
        node.textContent = String(saved.length);
      });

      document.querySelectorAll('[data-polene-wishlist]').forEach((button) => {
        const key = button.dataset.poleneWishlist || 'generic';
        const active = saved.includes(key);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    document.querySelectorAll('[data-polene-wishlist]').forEach((button) => {
      if (button.dataset.poleneWishlistBound === 'true') return;
      button.dataset.poleneWishlistBound = 'true';
      button.addEventListener('click', () => {
        const key = button.dataset.poleneWishlist || 'generic';
        saved = saved.includes(key) ? saved.filter((item) => item !== key) : [...saved, key];
        try {
          localStorage.setItem('polene-wishlist-items', JSON.stringify(saved));
        } catch (_) {
          // Le stockage privé peut être indisponible ; l’état visuel reste fonctionnel.
        }
        render();
      });
    });

    render();
  }

  function bindHomeReveals() {
    const nodes = [...document.querySelectorAll('.polene-home [data-polene-reveal]:not([data-polene-reveal-bound])')];
    if (!nodes.length) return;

    nodes.forEach((node) => { node.dataset.poleneRevealBound = 'true'; });

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.04 });

    nodes.forEach((node) => observer.observe(node));
  }

  function bindShuldoCarousel() {
    const section = document.querySelector('.polene-home-shuldo');
    const track = section?.querySelector('[data-polene-shuldo-track]');
    if (!section || !track || track.dataset.poleneCarouselBound === 'true') return;

    track.dataset.poleneCarouselBound = 'true';
    const desktop = window.matchMedia('(min-width: 1024px)');
    let frame = 0;
    let inViewport = false;

    const update = () => {
      frame = 0;
      if (!desktop.matches || !inViewport) {
        track.style.transform = '';
        return;
      }

      const rect = section.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const progress = Math.max(0, Math.min(1, (viewport - rect.top) / (viewport + rect.height)));
      const end = -Math.max(0, track.scrollWidth - window.innerWidth);
      track.style.transform = `translate3d(${end * progress}px, 0, 0)`;
    };

    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        inViewport = entries[0]?.isIntersecting === true;
        if (inViewport && desktop.matches) requestUpdate();
        if (!inViewport) track.style.transform = '';
      }, { rootMargin: '300px 0px' });
      observer.observe(section);
    } else {
      inViewport = true;
      requestUpdate();
    }

    desktop.addEventListener('change', requestUpdate);
    window.addEventListener('scroll', () => {
      if (inViewport && desktop.matches) requestUpdate();
    }, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
  }

  function bindCollectionInteractions() {
    document.querySelectorAll('[data-polene-family-carousel]').forEach((carousel) => {
      if (carousel.dataset.poleneCarouselBound === 'true') return;
      carousel.dataset.poleneCarouselBound = 'true';

      const viewport = carousel.querySelector('[data-polene-carousel-viewport]');
      const previous = carousel.querySelector('[data-polene-carousel-prev]');
      const next = carousel.querySelector('[data-polene-carousel-next]');
      if (!viewport) return;

      const updateArrows = () => {
        const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        if (previous) previous.disabled = viewport.scrollLeft <= 2;
        if (next) next.disabled = viewport.scrollLeft >= max - 2;
      };

      const scroll = (direction) => {
        const item = viewport.querySelector('li');
        const distance = item ? item.getBoundingClientRect().width : viewport.clientWidth * .25;
        viewport.scrollBy({ left: distance * direction, behavior: 'smooth' });
      };

      previous?.addEventListener('click', () => scroll(-1));
      next?.addEventListener('click', () => scroll(1));
      viewport.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows, { passive: true });
      updateArrows();
    });

    document.querySelectorAll('[data-polene-grid-density]').forEach((button) => {
      if (button.dataset.poleneDensityBound === 'true') return;
      button.dataset.poleneDensityBound = 'true';
      button.addEventListener('click', () => {
        const density = button.dataset.poleneGridDensity || 'large';
        const grid = document.querySelector('[data-polene-collection-grid]');
        if (!grid) return;
        grid.dataset.density = density;
        document.querySelectorAll('[data-polene-grid-density]').forEach((control) => {
          const active = control === button;
          control.classList.toggle('is-active', active);
          control.setAttribute('aria-pressed', String(active));
        });
      });
    });

    const gridContainer = document.querySelector('#ProductGridContainer');
    if (gridContainer && gridContainer.dataset.poleneObserverBound !== 'true') {
      gridContainer.dataset.poleneObserverBound = 'true';
      let wishlistFrame = 0;
      const observer = new MutationObserver(() => {
        if (wishlistFrame) return;
        wishlistFrame = window.requestAnimationFrame(() => {
          wishlistFrame = 0;
          bindWishlist();
        });
      });
      observer.observe(gridContainer, { childList: true, subtree: true });
    }
  }

  function init() {
    bindHeaderScroll();
    bindDrawers();
    bindMobileMenuPanels();
    bindDesktopMenus();
    bindWishlist();
    bindHomeReveals();
    bindShuldoCarousel();
    bindCollectionInteractions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
